import { authoredGeometry } from './authoredGeometry';
import { BakedCrowd } from './bakedCrowd';
import { SOLDIER_MUZZLES, SoldierWeaponVariants, weaponTier } from './soldierWeapons';
/**
 * Actor rendering: the squad (thin-instanced mannequins in formation), enemy blocks, the crate,
 * and the boss geometry/glow the rigged bosses are built from. All positions derive from sim state
 * (world.ts mapping).
 */
import {
  Constants,
  Color3,
  DynamicTexture,
  Matrix,
  Mesh,
  MeshBuilder,
  Quaternion,
  StandardMaterial,
  TransformNode,
  Vector3,
  VertexBuffer,
} from '@babylonjs/core/pure';
import type { Camera, PBRMaterial, Scene } from '@babylonjs/core/pure';
import type { RenderMaterials } from './scene';
import { BRIDGE_HALF_WIDTH } from './world';
import { effectiveFireInterval, type SimEvent, type SimState } from '../sim/sim';
import type { LevelSpec, GateSpec, ObstacleKind, WeaponSpec } from '../sim/level';
import { gateLabel, PICKUP_AHEAD, squadHalfX, WEAPONS } from '../sim/level';
import type { AtlasTile } from './soldierAtlas';
import { SOLDIER_HEAD_Y, SOLDIER_WAIST_Y } from './soldierAtlas';
import { BARREL_DIMS } from './barrelDims';

export function toWorldX(x: number): number {
  return x * BRIDGE_HALF_WIDTH;
}

/** Lateral metre spacing between neighbouring enemy instances (the squad's is `crowdPitch`). */
export const ENEMY_FORMATION_SPACING = 0.66;

// ---------------------------------------------------------------------------
// Plan 05 P5.3 — per-instance procedural motion, CLOSED-FORM.
//
// Contract (plan 05 Design 1 + §P5.3): every term below is a pure function of the CURRENT SimState
// snapshot + static level data. No render-layer state, no dt, no previous-frame memory, no RNG, no
// performance.now/clock. Consequences the tests pin:
//   * equal SimState ⇒ byte-identical instance matrices (screenshots reproduce under any
//     __jreb1dResume sequence, and the inspect pause freezes the crowd exactly);
//   * two fresh Squad/Enemies instances fed the same snapshot agree bit for bit (nothing mutable
//     survives between calls except the scratch temporaries, which are written before every read).
//
// Matrix = scale × rotation(yaw, pitch, roll) × translation, written with Matrix.ComposeToRef.
// The authored geometry pivots at the FEET (P5.2), so rotation/scale happen about the deck: no
// sliding, no sinking.
// ---------------------------------------------------------------------------

const TAU = Math.PI * 2;

/** Deck clearance the old placement used; kept identical so the formation term is unchanged. */
export const SQUAD_BASE_Y = 0.02;

export const MOTION = {
  /** Bob: 3.5 cm at 1.9 Hz, |sin| so the feet never sink below the deck (plan 05 §P5.3). */
  bobAmplitude: 0.035,
  bobHz: 1.9,
  /** Roll/sway: ±4° at half the step frequency, same phase family. */
  rollAmplitude: (4 * Math.PI) / 180,
  rollHz: 0.95,
  /** Squad yaw faces +z; this is only the per-slot standing-in-place jitter. */
  yawJitter: 0.05,
  /** Plan 10 A4: per-soldier stride rate bobHz × (1 ± 8 %) = 1.75–2.05 Hz (`gaitHz`). */
  gaitSpread: 0.08,
  /** Plan 10 A4: ~15 % of slots look around (±0.2 rad at 0.05–0.1 Hz) and lean (static ±0.05 rad). */
  lookShare: 0.15,
  lookYaw: 0.2,
  lookHz: 0.05,
  leanRoll: 0.05,
  /** Recoil decay constant (seconds) — the plan pins 0.07 and validates fireInterval > it. */
  recoilTau: 0.07,
  recoilScale: 0.03,
  recoilPitch: 0.06,
  /** Per-slot size variety 0.95–1.05, ≈ 1.69–1.87 m (Design 3: variety without vertex data). */
  scaleJitter: 0.05,
  /** Scramble ramp: 30 m of approach to the boss trigger, continuous in squadZ. */
  rampLength: 30,
  rampBobGain: 0.6,
  rampSwayGain: 0.4,
} as const;

/**
 * Stable per-slot hash (0..1). Literal arithmetic only — the plan-05 "fract(sin(…)·43758)" family,
 * with an additive offset so slot (0,0) is not the degenerate sin(0) = 0 phase.
 */
export function stableHash(row: number, col: number, stream = 0): number {
  const s = Math.sin(row * 127.1 + col * 311.7 + stream * 74.7 + 13.7) * 43758.5453;
  return s - Math.floor(s);
}

/**
 * Per-soldier colour variation (plan 08 G5): value ±4 % and a red/blue hue skew ±2 % from two hashed
 * streams, so every channel stays within ±6.1 % of 1. Pure in (a, b) — deterministic, no RNG.
 */
export const TINT_VALUE = 0.04;
export const TINT_HUE = 0.02;
export function slotTint(a: number, b: number): [number, number, number] {
  const v = 1 + TINT_VALUE * (2 * a - 1);
  const h = TINT_HUE * (2 * b - 1);
  return [v * (1 + h), v, v * (1 - h)];
}
function writeTint(buf: Float32Array, i: number, a: number, b: number): void {
  const [r, g, bl] = slotTint(a, b);
  buf[i * 4] = r;
  buf[i * 4 + 1] = g;
  buf[i * 4 + 2] = bl;
  buf[i * 4 + 3] = 1;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Continuous urgency ramp into the boss fight (replaces the r2 phase-gated amplitude step). */
export function scrambleRamp(squadZ: number, bossTriggerZ: number): number {
  return clamp01((squadZ - (bossTriggerZ - MOTION.rampLength)) / MOTION.rampLength);
}

/** Bob offset (metres, ≥ 0) for one slot at time t (the squad passes its per-slot `gaitHz`). */
export function bobY(t: number, hash: number, ramp: number, hz: number = MOTION.bobHz): number {
  const amp = MOTION.bobAmplitude * (1 + MOTION.rampBobGain * ramp);
  return amp * Math.abs(Math.sin(TAU * hz * t + hash * TAU));
}

/** Roll (radians) for one slot at time t — same phase family, half the frequency. */
export function rollRad(t: number, hash: number, ramp: number, hz: number = MOTION.rollHz): number {
  const amp = MOTION.rollAmplitude * (1 + MOTION.rampSwayGain * ramp);
  return amp * Math.sin(TAU * hz * t + hash * TAU);
}

/** Plan 10 A4: the stride rate (Hz) of squad slot `slot` — bob, sway (at half) and walk clip. */
export function gaitHz(slot: number): number {
  return gaitBaseHz * (1 + MOTION.gaitSpread * (2 * stableHash(slot, 18) - 1));
}

/** Plan 24 (impl-gate r1 copilot P0-2): the gait BASE rate follows the level theme's
 *  march grid (8th notes = bpm/30 ≈ 3.33–3.87 Hz at 100–116 BPM — the authentic quick-march
 *  feel) instead of the fixed 1.9 Hz; the ±8 % per-slot spread stays (crowd realism).
 *  strideScaleForBpm documents the constant-displacement arithmetic (world speed is the
 *  sim's; the bob only needs to READ as marching at the theme tempo). */
let gaitBaseHz: number = MOTION.bobHz;
export function setGaitBaseHz(hz: number): void {
  gaitBaseHz = Number.isFinite(hz) && hz > 0.5 && hz < 8 ? hz : MOTION.bobHz;
}

// shortcut: keep the shipped in-place gait until its clip can apply stride scaling, retime after visual review.
export function strideScaleForBpm(bpm: number): number {
  return MOTION.bobHz / (bpm / 30);
}

/**
 * Recoil amplitude (0..1) for one instance, closed-form from the fire-cooldown saw-tooth.
 *
 * `fireCooldown` wraps to just-under `interval` at each volley and decays to 0, so `τ = interval −
 * fireCooldown` is the age of the most recent shot. Gate: zero outside `0 < fireCooldown ≤ interval`
 * — no flinch at rest, and an out-of-range hold (e.g. a crate upgrade shrinking the interval) rests
 * at zero instead of snapping to peak. Per-instance onset is an unclamped TIME DELAY (δ_i), so 48
 * soldiers do not flinch on the same frame and none parks at peak behind a clamped τ.
 */
export function recoilAmp(fireCooldown: number, interval: number, hash: number): number {
  if (!(fireCooldown > 0) || !(fireCooldown <= interval)) return 0;
  const tau = interval - fireCooldown;
  const onset = (hash - Math.floor(hash)) * Math.min(MOTION.recoilTau, interval);
  const age = tau - onset;
  if (age < 0) return 0;
  return Math.exp(-age / MOTION.recoilTau);
}

/** The wedge formation slot of instance `index` (mirrors Squad.update's layout exactly). */
export function formationSlot(index: number, shown: number): { row: number; col: number; inRow: number } {
  let i = 0;
  for (let row = 0; i < shown; row++) {
    const inRow = Math.min(4 + row, shown - i);
    if (index < i + inRow) return { row, col: index - i, inRow };
    i += inRow;
  }
  return { row: 0, col: 0, inRow: 0 }; // unreachable for index < shown
}

/** Number of wedge rows a crowd of `shown` fills: 4, 5, 6, … soldiers wide. */
export function formationRowCount(shown: number): number {
  let i = 0;
  let row = 0;
  while (i < shown) {
    i += Math.min(4 + row, shown - i);
    row++;
  }
  return row;
}

/**
 * Depth budget of the squad crowd (metres, backward from squadZ).
 *
 * Plan-05 P5.3 formation compaction (lane T2 amendment): the chase rail is FROZEN 8 m behind the
 * squad (main.ts, plan 03 framing contract — camera moves are not the lever), and the wedge's
 * back rows are the instances CLOSEST to it. At the high-tier cap (48 soldiers ⇒ 7 rows) the
 * legacy 0.496 m pitch stood the deepest feet 2.976 m behind squadZ, which projects to
 * yFromTop 1.064 — off the bottom of the viewport, against the probe's ≤0.98 assert (measured by
 * T1 at High; `actors.test.ts` re-derives the same number from the rail math).
 *
 * So the pitch compresses instead: the crowd keeps a hard depth budget behind squadZ at every
 * tier. The budget is 1.9 m, which puts the deepest feet at yFromTop ≈ 0.949 (the 0.98 gate plus
 * margin for the bob, which only ever lifts feet). `crowdPitch` builds every row pitch and depth
 * lag inside it.
 */
export const MAX_FORMATION_DEPTH = 1.9;

/**
 * Plan 10 A2 — the density-adaptive crowd. Same wedge topology (formationSlot keeps slot identity),
 * but the lateral pitch p and row pitch q are as loose as the two hard bounds allow, and per-slot
 * jitter + drift get exactly the clearance left over: a small crowd is loose, a High-cap crowd is a
 * dense press. Torso-core ellipse clearance (half-axes at the max scale 1.05; authored torso x ±0.21,
 * z −0.11..0.19) holds at rest by construction: neighbours stay ≥ 2a apart in a row and ≥ 2b
 * between rows. Arms and pack are not covered (they already clip under the roll sway).
 */
export const CROWD = {
  /** Loosest lateral / row pitch (m), what a small crowd spreads to. */
  looseP: 0.8,
  looseQ: 0.6,
  /** Share of the leftover clearance given to per-slot offsets, and the slow drift's share of that. */
  jitterShare: 0.8,
  driftShare: 0.35,
  torsoA: 0.21 * 1.05,
  torsoB: 0.15 * 1.05,
} as const;

/** The loosest pitch ≤ `loose` with gaps·x + k·(x − w) ≤ budget; no jitter room once budget/gaps < w. */
function fitPitch(budget: number, gaps: number, w: number, k: number, loose: number): number {
  if (gaps > 0 && budget / gaps < w) return budget / gaps;
  return Math.min(loose, (budget + w * k) / (gaps + k));
}

/** The legacy wedge's lateral pitch (m): no crowd spreads wider than that wedge did at its size. */
const WEDGE_PITCH = 0.62;

/**
 * Pitches and offset amplitudes for a crowd of `shown`: every centre within the lateral bound of the
 * squad x (±jitterX at the widest row's edge) and every z-lag within [0, MAX_FORMATION_DEPTH]
 * (lagMax behind the back row), for any row count. The lateral bound is the sim footprint
 * (SQUAD_FOOTPRINT_HALF_X × BRIDGE_HALF_WIDTH), capped at the legacy wedge's half-extent for the same
 * shown so a mid-size crowd never spreads wider than the frame the old wedge fit (coordinator, plan 10).
 */
export function crowdPitch(shown: number): { rows: number; widest: number; p: number; q: number; jitterX: number; lagMax: number } {
  const rows = formationRowCount(shown);
  const widest = rows > 1 ? Math.max(rows + 2, formationSlot(shown - 1, shown).inRow) : Math.max(0, shown);
  const k = CROWD.jitterShare;
  const half = Math.max(0, widest - 1) / 2;
  // Plan 13: every torso stays inside the footprint the sim tests at this size (squadHalfX grows with
  // n and shown ≤ n, so the crowd is never wider than the hitbox the marker draws).
  const halfWidth = Math.max(0, Math.min(squadHalfX(shown) * BRIDGE_HALF_WIDTH - CROWD.torsoA, half * WEDGE_PITCH));
  const p = fitPitch(halfWidth, half, 2 * CROWD.torsoA, k / 2, CROWD.looseP);
  const q = fitPitch(MAX_FORMATION_DEPTH, rows - 1, 2 * CROWD.torsoB, k, CROWD.looseQ);
  return { rows, widest, p, q, jitterX: (k * Math.max(0, p - 2 * CROWD.torsoA)) / 2, lagMax: k * Math.max(0, q - 2 * CROWD.torsoB) };
}

/**
 * The static level data the motion needs. Structurally satisfied by `LevelSpec`, so a caller may
 * hand the whole level over; the shipped boot path defaults to the published weapon ladder and the
 * shipped boss trigger z (both levels trigger within 10 m of it, and the ramp is 30 m wide).
 */
export interface MotionLevel {
  weapons: WeaponSpec[];
  boss?: { z: number };
}

/** L1's boss z (L2 is 470): the fallback trigger used when the caller does not pass a level. */
export const DEFAULT_BOSS_TRIGGER_Z = 480;

const DEFAULT_MOTION_LEVEL: MotionLevel = { weapons: WEAPONS, boss: { z: DEFAULT_BOSS_TRIGGER_Z } };

/** One soldier's root transform terms (world metres / radians), written by `crowdSlot`. */
export interface CrowdPose {
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
  roll: number;
  scale: number;
  /** Gait phase identity (stableHash(row, col)) and stride rate (gaitHz) — bob/roll/walk clip. */
  phase: number;
  hz: number;
}

export function crowdPose(): CrowdPose {
  return { x: 0, y: 0, z: 0, yaw: 0, pitch: 0, roll: 0, scale: 1, phase: 0, hz: MOTION.bobHz };
}

/** Plan 10 A3: seconds the crowd takes to glide to its new layout after n changes (sim.crowdAt). */
export const CROWD_SETTLE_S = 0.6;

// Scratch only (P5.3): written by crowdRest before every read.
const rest = { dx: 0, lag: 0 };

/**
 * At-rest lateral offset (m, from the squad x) and backward z-lag (m) of `slot` among `shown` at t.
 * Static hashed offset + a slow 0.09–0.24 Hz drift share the one amplitude budget (|sx| ≤ 1,
 * sz ∈ [0, 1]), so the crowdPitch bounds hold at every t. The lag is backward only.
 */
function crowdRest(slot: number, shown: number, t: number): typeof rest {
  const { row, col, inRow } = formationSlot(slot, shown);
  const c = crowdPitch(shown);
  const d = CROWD.driftShare;
  const sx = (1 - d) * (2 * stableHash(slot, 11) - 1) + d * Math.sin(TAU * (0.11 + 0.13 * stableHash(slot, 15)) * t + TAU * stableHash(slot, 13));
  const sz = (1 - d) * stableHash(slot, 12) + d * (0.5 + 0.5 * Math.sin(TAU * (0.09 + 0.13 * stableHash(slot, 16)) * t + TAU * stableHash(slot, 14)));
  rest.dx = (col - (inRow - 1) / 2) * c.p + c.jitterX * sx;
  rest.lag = row * c.q + c.lagMax * sz;
  return rest;
}

/**
 * Plan 10 A1 — the ONE placement function for a squad soldier. Squad, SkeletalSquad, the Fx muzzle
 * flashes and main.ts's blob sizing all read the crowd through this, so they cannot drift apart.
 * Pure in (slot, cap, snapshot, static level) like every P5.3 term; `out` is caller scratch.
 *
 * Settle (A3): for CROWD_SETTLE_S after n changes, survivors (slot < crowdFrom) glide from their
 * old-layout spot to the new one, and recruits run in from the back edge of the depth budget at
 * their final x — closed-form in (t − crowdAt), so a paused frame still freezes exactly.
 *
 * `corpseShown > 0` places a corpse: laid out at rest in a formation of that size, with no motion
 * terms (the skeletal death clip owns the body from there).
 */
export function crowdSlot(slot: number, cap: number, sim: SimState, level: MotionLevel, out: CrowdPose, corpseShown = 0): CrowdPose {
  const dead = corpseShown > 0;
  const shown = dead ? corpseShown : Math.min(sim.n, cap);
  const { row, col } = formationSlot(slot, shown);
  const phase = stableHash(row, col);
  const size = stableHash(row, col, 1);
  const ramp = scrambleRamp(sim.squadZ, level.boss?.z ?? DEFAULT_BOSS_TRIGGER_Z);
  const amp = dead ? 0 : recoilAmp(sim.fireCooldown, effectiveFireInterval(level.weapons, sim), phase);
  // A corpse's drift is frozen (t = 0): bodies do not shuffle.
  const tDrift = dead ? 0 : sim.t;
  let { dx, lag } = crowdRest(slot, shown, tDrift);
  const from = Math.min(sim.crowdFrom, cap);
  const u = dead || from === shown ? 1 : clamp01((sim.t - sim.crowdAt) / CROWD_SETTLE_S);
  if (u < 1) {
    const s = u * u * (3 - 2 * u);
    const old = slot < from ? crowdRest(slot, from, tDrift) : { dx, lag: MAX_FORMATION_DEPTH };
    dx = old.dx + (dx - old.dx) * s;
    lag = old.lag + (lag - old.lag) * s;
  }
  out.x = toWorldX(sim.squadX) + dx;
  out.z = sim.squadZ - lag;
  const hz = gaitHz(slot);
  const looker = !dead && stableHash(slot, 17) < MOTION.lookShare;
  out.y = SQUAD_BASE_Y + (dead ? 0 : bobY(sim.t, phase, ramp, hz));
  // Squad faces +z (toward the enemies) — the authored rifle convention; standing jitter + a look.
  const look = looker ? MOTION.lookYaw * Math.sin(TAU * MOTION.lookHz * (1 + stableHash(slot, 17, 1)) * sim.t + TAU * stableHash(slot, 17, 2)) : 0;
  out.yaw = dead ? 0 : (size - 0.5) * 2 * MOTION.yawJitter + look;
  // Positive pitch tips the nose up: muzzle-rise + a backward brace about the feet pivot.
  out.pitch = MOTION.recoilPitch * amp;
  const lean = looker ? (stableHash(slot, 17, 3) < 0.5 ? -MOTION.leanRoll : MOTION.leanRoll) : 0;
  out.roll = dead ? 0 : rollRad(sim.t, phase, ramp, hz / 2) + lean;
  out.scale = (1 + MOTION.recoilScale * amp) * (1 - MOTION.scaleJitter + 2 * MOTION.scaleJitter * size);
  out.phase = phase;
  out.hz = hz;
  return out;
}

/** Centre-to-centre width × depth (metres) the crowd of `shown` can occupy — the blob footprint. */
export function crowdExtent(shown: number): { width: number; depth: number } {
  const c = crowdPitch(shown);
  return { width: Math.max(0, c.widest - 1) * c.p + 2 * c.jitterX, depth: Math.max(0, c.rows - 1) * c.q + c.lagMax };
}

/**
 * Probe slots by written position, not by index (jitter breaks index ⇒ position): front = max z,
 * back = min z, edge = the outermost (max |x|) soldier of the FRONT rank (slots 0..3 of the
 * `alive` living soldiers) — the leading rank must stay readable. The pre-plan-10 edge sample was
 * slot 2, an interior front-row soldier, so it never tested an edge; back/side ranks may crop at
 * High, as the legacy wedge already did (xFromLeft −0.117..1.117 at 48 on the centre line).
 */
export function framingSlots(matrices: Float32Array, count: number, alive = count): { front: number; back: number; edge: number } {
  let front = count > 0 ? 0 : -1;
  let back = front;
  let edge = front;
  for (let i = 1; i < count; i++) {
    if (matrices[i * 16 + 14]! > matrices[front * 16 + 14]!) front = i;
    if (matrices[i * 16 + 14]! < matrices[back * 16 + 14]!) back = i;
    if (i < Math.min(4, alive) && Math.abs(matrices[i * 16 + 12]!) > Math.abs(matrices[edge * 16 + 12]!)) edge = i;
  }
  return { front, back, edge };
}

// ---------------------------------------------------------------------------
// P5.2 — authored soldier (plan 05 §P5.2).
//
// Modern-infantry silhouette assembled from primitives, ONE merged mesh, ONE material per
// faction, UV-atlassed per part (soldierAtlas.ts) so the P5.5 procedural material can paint
// camo on the uniform and black on the rifle instead of painting the same 0..1 square twice.
//
// Authoring rules that the tests pin:
//  * pivot at the FEET (minY ≈ 0) — P5.3 rotates/scales about the origin, a centred origin makes
//    the feet slide/sink;
//  * total height 1.75–1.85 m, half-width ≤ the tightest crowd pitch/2 (crowdPitch(48).p/2 ≈ 0.30)
//    so neighbours never interpenetrate;
//  * explicit low tessellation on every primitive (segments/tessellation ≤ 8): Babylon's
//    defaults blow the 950-triangle budget on the third part;
//  * no RNG — the stance is authored, including the slight forward lean (a spine pivot rotation
//    baked by MergeMeshes), which replaces the cut smoothed steering lean (plan 05 P5.3).
// ---------------------------------------------------------------------------

export interface CharacterPartRange {
  name: string;
  tile: AtlasTile;
  /** Vertex range inside the MERGED mesh — MergeMeshes concatenates in source order. */
  vertexStart: number;
  vertexEnd: number;
  triangles: number;
}

export interface CharacterGeometry {
  mesh: Mesh;
  parts: CharacterPartRange[];
}

/** Naming compatibility: the soldier and the boss share the authored-part bookkeeping. */
export type SoldierGeometry = CharacterGeometry;
export type BossGeometry = CharacterGeometry;

function box(
  scene: Scene,
  name: string,
  w: number,
  h: number,
  d: number,
  x: number,
  y: number,
  z: number,
  parent?: TransformNode,
): Mesh {
  const mesh = MeshBuilder.CreateBox(name, { width: w, height: h, depth: d }, scene);
  mesh.position.set(x, y, z);
  if (parent) mesh.parent = parent;
  return mesh;
}

/**
 * The authored soldier. Returns the merged mesh plus the per-part vertex ranges/tiles, which the
 * tests use to prove the atlas remap actually landed (head UVs ⊂ skin tile, rifle UVs ⊂ weapon
 * tile) — the merged product has one submesh, so ranges are the only way back to the parts.
 */
export function buildSoldier(scene: Scene, material: PBRMaterial, name: string): SoldierGeometry {
  return authoredGeometry('soldier', scene, material, name);
}

/**
 * One MergeMeshes product + the per-part vertex ranges the tests need to audit the atlas.
 * Ranges are captured BEFORE the merge, which disposes the sources; MergeMeshes concatenates
 * vertex data in source order and bakes each source's world matrix (so parenting a part to a
 * TransformNode is how a baked pose/lean is authored).
 */

/** Blob shadow: soft radial disc under actors (cheap stand-in for cast shadows, plan 02).
 * Falls back to a flat translucent disc when no 2D context exists (NullEngine in tests).
 * Exported for plan 07's SkeletalSquad (same pooled-disc discipline, same material).
 * Alphas calibrated to the High tier's real cascade (plan 08 G1): the crowd's shadow lobe there darkens
 * the deck to ≈0.86 of open-deck luminance, so the overlapped blobs must land at the same ratio. */
export function buildShadowTexture(scene: Scene, name = 'shadow-mat'): StandardMaterial {
  const size = 128;
  const mat = new StandardMaterial(name, scene);
  const canGradient = typeof OffscreenCanvas !== 'undefined';
  if (canGradient) {
    const dyn = new DynamicTexture('shadow-tex', size, scene, false);
    const ctx = dyn.getContext() as CanvasRenderingContext2D | null;
    if (ctx && typeof ctx.createRadialGradient === 'function') {
      const grad = ctx.createRadialGradient(size / 2, size / 2, 4, size / 2, size / 2, size / 2);
      grad.addColorStop(0, 'rgba(0,0,0,0.2)');
      grad.addColorStop(0.6, 'rgba(0,0,0,0.1)');
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, size, size);
      dyn.update();
      dyn.hasAlpha = true;
      mat.diffuseTexture = dyn;
      mat.opacityTexture = dyn;
    } else {
      mat.diffuseColor = new Color3(0, 0, 0);
      mat.alpha = 0.11;
    }
  } else {
    // Node/NullEngine: no OffscreenCanvas — flat translucent disc (tests only see geometry).
    mat.diffuseColor = new Color3(0, 0, 0);
    mat.alpha = 0.11;
  }
  mat.disableLighting = true;
  mat.emissiveColor = new Color3(0, 0, 0);
  mat.backFaceCulling = false;
  mat.zOffset = -2; // keep off z-fighting with the deck
  return mat;
}

// ---------------------------------------------------------------------------
// Blob ground shadows (the plan-02 read; plan 05 §P5.6 keeps them for the ENEMIES on every tier —
// the enemy mesh is deliberately not a shadow caster, so without the blob a block would float).
//
// ONE pooled mesh per crowd (never one per body): Squad owns `squad-shadow`, Enemies owns
// `enemy-shadow`. Both are authored as a BLOB_AUTHORED_GROUND² ground and SCALED to the crowd's
// formation footprint, so the disc always matches the geometry the same snapshot lays down — the
// sizing is a pure function of the shown count, hence pause-frozen like everything else here.
// ---------------------------------------------------------------------------

/** The authored size (metres) of every blob ground quad. The scale factors are relative to this. */
export const BLOB_AUTHORED_GROUND = 2.6;
/** Extra ground beyond the crowd's centre-to-centre footprint: half-bodies + the gradient's soft edge. */
export const BLOB_MARGIN = 0.8;

/** Scale a BLOB_AUTHORED_GROUND² ground over a `width` × `depth` metre footprint (y stays 1: flat deck). */
export function blobScaling(width: number, depth: number): { x: number; y: number; z: number } {
  return {
    x: Math.max(0, width + BLOB_MARGIN) / BLOB_AUTHORED_GROUND,
    y: 1,
    z: Math.max(0, depth + BLOB_MARGIN) / BLOB_AUTHORED_GROUND,
  };
}

/** Bodies per enemy row for the default block (rows of ≤ 5, front row at enemy.z). */
export const ENEMY_BLOCK_WIDTH = 5;
/** Half a body's shoulder width (m): the outer bodies' edges, not centres, meet the hit range. */
const ENEMY_BODY_HALF = 0.3;

/**
 * Plan 13: bodies per row so the drawn block spans its hit range (x ± halfWidth): the widest row's
 * edges reach toWorldX(halfWidth) without passing it. Bullets and contact test that range.
 */
export function enemyBlockColumns(halfWidth: number): number {
  const span = 2 * halfWidth * BRIDGE_HALF_WIDTH - 2 * ENEMY_BODY_HALF;
  return Math.max(1, 1 + Math.floor(span / ENEMY_FORMATION_SPACING));
}
/** Depth pitch between adjacent enemy rows, as a fraction of the lateral spacing. */
export const ENEMY_ROW_DEPTH_FACTOR = 0.7;

/**
 * Centre-to-centre footprint (metres) of the block layout for `shown` bodies, derived from the SAME
 * terms `Enemies.update` uses to place them (ENEMY_BLOCK_WIDTH wide, ENEMY_FORMATION_SPACING apart,
 * rows receding by spacing × ENEMY_ROW_DEPTH_FACTOR from the block's z). The blob is sized from this,
 * so the disc can never disagree with the crowd.
 */
export function enemyBlockFootprint(shown: number, cols = ENEMY_BLOCK_WIDTH): {
  rows: number;
  widest: number;
  width: number;
  depth: number;
} {
  const visible = Math.max(0, shown);
  const rows = Math.max(1, Math.ceil(visible / cols));
  const widest = Math.min(cols, visible);
  return {
    rows,
    widest,
    width: Math.max(0, widest - 1) * ENEMY_FORMATION_SPACING,
    depth: (rows - 1) * ENEMY_FORMATION_SPACING * ENEMY_ROW_DEPTH_FACTOR,
  };
}

export const CHEAP_CASUALTY_HOLD = 1.4;
export const CHEAP_CASUALTY_CAP = 4;

export class Squad {
  private aliveCount = 0;
  private readonly mesh: Mesh;
  private readonly baked: BakedCrowd | null;
  private readonly weapons: SoldierWeaponVariants;
  private currentTier = 0;
  private readonly tints: Float32Array;
  private readonly shadow: Mesh;
  // audit #11: main.ts retires the blob once on High (the real cascade grounds the squad); update()
  // honours this flag instead of the mesh's own enabled state, so that boot disable survives the frame.
  private blobEnabled = true;
  private readonly matrices: Float32Array;
  private readonly tmp = Matrix.Identity();
  // Scratch only: written before every read, holds nothing across frames (P5.3 purity).
  private readonly scratchScale = new Vector3(1, 1, 1);
  private readonly scratchPos = new Vector3(0, 0, 0);
  private readonly scratchRot = Quaternion.Identity();
  private readonly scratchPose = crowdPose();

  constructor(
    scene: Scene,
    materials: RenderMaterials,
    maxInstances: number,
    private readonly level: MotionLevel = DEFAULT_MOTION_LEVEL,
  ) {
    const geometry = buildSoldier(scene, materials.soldier, 'soldier');
    this.mesh = geometry.mesh;
    this.baked = scene.getEngine().getClassName() !== 'NullEngine' && scene.getEngine().getCaps().textureFloat
      ? new BakedCrowd(scene, this.mesh, geometry.parts, maxInstances)
      : null;
    this.weapons = new SoldierWeaponVariants(scene, materials.soldier, geometry);
    this.matrices = new Float32Array(maxInstances * 16);
    for (let i = 0; i < maxInstances; i++) {
      Matrix.IdentityToRef(this.tmp);
      this.tmp.copyToArray(this.matrices, i * 16);
    }
    this.mesh.thinInstanceSetBuffer('matrix', this.matrices, 16, false);
    // Static per-slot tint (stride 4 is REQUIRED: the default colour stride is 16). Keyed by slot
    // index, so a soldier keeps its shade as the squad grows and shrinks.
    this.tints = new Float32Array(maxInstances * 4);
    for (let i = 0; i < maxInstances; i++) writeTint(this.tints, i, stableHash(i, 7, 2), stableHash(i, 7, 3));
    this.mesh.thinInstanceSetBuffer('color', this.tints, 4, false);
    this.mesh.thinInstanceCount = 0;
    this.shadow = MeshBuilder.CreateGround('squad-shadow', { width: BLOB_AUTHORED_GROUND, height: BLOB_AUTHORED_GROUND }, scene);
    this.shadow.material = buildShadowTexture(scene);
    
  }

  update(sim: SimState, maxInstances: number): void {
    this.currentTier = weaponTier(sim.weaponTier);
    this.weapons.setTier(sim.weaponTier);
    const cap = Math.min(maxInstances, this.matrices.length / 16);
    const alive = Math.min(sim.n, cap);
    this.aliveCount = alive;
    let shown = 0;
    const place = (slot: number, age: number | null): void => {
      const pose = crowdSlot(slot, cap, sim, this.level, this.scratchPose,
        age === null ? 0 : Math.max(slot + 1, Math.min(sim.crowdFrom, cap)));
      if (age === null) this.baked?.slot(shown, pose.phase, pose.hz, sim.phase === 'run');
      else this.baked?.death(shown, age);
      this.scratchScale.set(pose.scale, pose.scale, pose.scale);
      const sink = age === null ? 0 : Math.max(0, (age - 1.1) / 0.3) * 0.7;
      this.scratchPos.set(pose.x, pose.y - sink, pose.z);
      const fall = age === null || this.baked ? 0 : -1.35 * Math.min(1, age / 0.9);
      Quaternion.RotationYawPitchRollToRef(pose.yaw, pose.pitch + fall, pose.roll, this.scratchRot);
      Matrix.ComposeToRef(this.scratchScale, this.scratchRot, this.scratchPos, this.tmp);
      this.tmp.copyToArray(this.matrices, shown * 16);
      writeTint(this.tints, shown, stableHash(slot, 7, 2), stableHash(slot, 7, 3));
      shown++;
    };
    for (let slot = 0; slot < alive; slot++) place(slot, null);
    const slots = new Set<number>();
    for (let i = sim.losses.length - 1; i >= 0 && shown < cap && slots.size < CHEAP_CASUALTY_CAP; i--) {
      const loss = sim.losses[i]!;
      const age = sim.t - loss.t;
      if (age >= CHEAP_CASUALTY_HOLD) break; // losses are appended in simulation-time order
      if (loss.slot < sim.n || loss.slot >= cap || age < 0 || slots.has(loss.slot)) continue;
      slots.add(loss.slot);
      place(loss.slot, age);
    }
    this.mesh.thinInstanceCount = shown;
    this.mesh.thinInstanceBufferUpdated('matrix');
    this.mesh.thinInstanceBufferUpdated('color');
    this.baked?.update(sim.t);
    // Thin instances render with the base mesh — refresh its bounds so frustum culling
    // follows the instances instead of the stale origin bbox (plan 03 root cause 4).
    this.mesh.thinInstanceRefreshBoundingInfo(true);
    this.shadow.position.x = toWorldX(sim.squadX);
    this.shadow.position.z = sim.squadZ - 0.6;
    // audit #11: the disc has to die with the crowd. main.ts sizes it (sizeSquadBlob) but that
    // early-returns at 0, so through the SQUAD WIPED overlay the last scale used to stay on the deck.
    // Same rule as Enemies.updateBlob: nothing live ⇒ no disc.
    this.shadow.setEnabled(this.blobEnabled && alive > 0);
  }

  muzzlePosition(slot: number): Vector3 | null {
    if (slot < 0 || slot >= this.aliveCount) return null;
    const point = Vector3.FromArray(SOLDIER_MUZZLES[this.currentTier]!);
    const posed = this.baked ? this.baked.muzzle(slot, point) : point;
    return Vector3.TransformCoordinates(posed, Matrix.FromArray(this.matrices, slot * 16));
  }

  /**
   * audit #11: retire the blob for good — main.ts's High-tier path calls this INSTEAD of reaching
   * into the scene for the `squad-shadow` mesh, because update() re-asserts the disc every frame; a
   * bare `mesh.setEnabled(false)` there would be undone on the very next snapshot.
   */
  disableBlob(): void {
    this.blobEnabled = false;
    this.shadow.setEnabled(false);
  }

  /**
   * Probe (P5.3): the two unit-test-locked bob samples — instance 0 (front row, centre-left) and
   * the last shown instance (back row). Read back off the written matrices, minus the deck
   * clearance, so the probe reports the MOTION term rather than the placement term.
   * The same slot pair is what actors.test.ts locks for the alias check; main.ts wires this into
   * the `?debug=1` payload as `anim: squad.debugAnim()`.
   */
  debugAnim(): { frontBobY: number; backBobY: number; method: 'procedural' | 'baked'; pure: true } {
    const count = this.mesh.thinInstanceCount;
    const bob = (i: number): number => (i >= 0 && i < count ? this.matrices[i * 16 + 13]! - SQUAD_BASE_Y : 0);
    return { frontBobY: bob(0), backBobY: bob(count - 1), method: this.baked ? 'baked' : 'procedural', pure: true };
  }

  /** Test handle: the thin-instance tint buffer (RGBA, stride 4). */
  debugTints(): Float32Array {
    return this.tints;
  }

  /** Test/probe handle: a copy of one instance matrix (16 floats, row-major). */
  debugMatrix(i: number): number[] {
    return Array.from(this.matrices.subarray(i * 16, i * 16 + 16));
  }

  /** Probe: thin-instance z extents (plan 03 Phase 1). */
  debugThinInstances(): { name: string; count: number; zMin: number | null; zMax: number | null } {
    let zMin: number | null = null;
    let zMax: number | null = null;
    for (let i = 0; i < this.mesh.thinInstanceCount; i++) {
      const z = this.matrices[i * 16 + 14];
      zMin = zMin === null ? z : Math.min(zMin, z);
      zMax = zMax === null ? z : Math.max(zMax, z);
    }
    return { name: 'soldier', count: this.mesh.thinInstanceCount, zMin, zMax };
  }

  /**
   * Probe: framing of the ACTUAL instance matrices (opus r3 N3 — never sim intent).
   * Fractions measured from the TOP/LEFT in a unit viewport (y-down, Babylon convention);
   * points behind the camera report { depth: 'behind' } instead of fractions.
   */
  debugFraming(camera: Camera): Record<string, unknown> {
    const count = this.mesh.thinInstanceCount;
    const at = (i: number, dy: number): Vector3 | null =>
      i >= 0 && i < count
        ? new Vector3(this.matrices[i * 16 + 12], this.matrices[i * 16 + 13] + dy, this.matrices[i * 16 + 14])
        : null;
    const project = (v: Vector3): Record<string, number | string> => {
      // Babylon view space is left-handed: points in front have POSITIVE view z.
      const view = Vector3.TransformCoordinates(v, camera.getViewMatrix(true));
      if (view.z <= camera.minZ) return { depth: 'behind' };
      const p = Vector3.Project(v, Matrix.Identity(), camera.getTransformationMatrix(), camera.viewport.toGlobal(1, 1));
      return { yFromTop: p.y, xFromLeft: p.x, depth: view.z };
    };
    const { front, back, edge } = framingSlots(this.matrices, count, this.aliveCount);
    return {
      frontRowHead: at(front, SOLDIER_HEAD_Y) ? project(at(front, SOLDIER_HEAD_Y)!) : null,
      backRowFeet: at(back, 0) ? project(at(back, 0)!) : null,
      edgeSoldier: at(edge, SOLDIER_WAIST_Y) ? project(at(edge, SOLDIER_WAIST_Y)!) : null,
      instanceCount: count,
    };
  }
}

export class Enemies {
  private readonly contacts: Float32Array;
  private enemyContact = false;
  private readonly mesh: Mesh;
  private readonly baked: BakedCrowd | null;
  private readonly shadow: Mesh;
  private readonly matrices: Float32Array;
  private readonly tmp = Matrix.Identity();
  private readonly tints: Float32Array;
  // Scratch only (see Squad): no motion input survives between calls.
  private readonly scratchScale = new Vector3(1, 1, 1);
  private readonly scratchPos = new Vector3(0, 0, 0);
  private readonly scratchRot = Quaternion.Identity();

  constructor(
    scene: Scene,
    materials: RenderMaterials,
    private readonly maxInstances: number,
    private readonly level: MotionLevel = DEFAULT_MOTION_LEVEL,
    private readonly contactLevel?: Pick<LevelSpec, 'crates' | 'obstacles'>,
  ) {
    const geometry = buildSoldier(scene, materials.enemy, 'enemy-soldier');
    this.mesh = geometry.mesh;
    this.baked = scene.getEngine().getClassName() !== 'NullEngine' && scene.getEngine().getCaps().textureFloat
      ? new BakedCrowd(scene, this.mesh, geometry.parts, maxInstances, true)
      : null;
    this.matrices = new Float32Array(maxInstances * 16);
    this.tints = new Float32Array(maxInstances * 4).fill(1);
    this.mesh.thinInstanceSetBuffer('matrix', this.matrices, 16, false);
    this.mesh.thinInstanceSetBuffer('color', this.tints, 4, false);
    this.mesh.thinInstanceCount = 0;
    // Plan 05 §P5.6 / impl gate F2: the enemy crowd's grounding. ONE pooled blob for the whole
    // block list (never one per body), on EVERY tier — the enemy mesh is deliberately not a shadow
    // caster, so this disc is the only thing holding the block to the deck. `update` enables it only
    // while at least one block is live; before the first `update` it is disabled, so a crowd that
    // never exists is never grounded by a stale disc.
    this.shadow = MeshBuilder.CreateGround('enemy-shadow', { width: BLOB_AUTHORED_GROUND, height: BLOB_AUTHORED_GROUND }, scene);
    this.shadow.material = buildShadowTexture(scene, 'enemy-shadow-mat');
    this.contacts = new Float32Array((1 + (contactLevel?.crates.length ?? 0) + (contactLevel?.obstacles.length ?? 0)) * 16);
    this.shadow.thinInstanceSetBuffer('matrix', this.contacts, 16, false);
    this.shadow.thinInstanceCount = 0;
    this.shadow.setEnabled(false);
  }

  update(sim: SimState): void {
    const t = sim.t;
    const ramp = scrambleRamp(sim.squadZ, this.level.boss?.z ?? DEFAULT_BOSS_TRIGGER_Z);
    const squadX = toWorldX(sim.squadX);
    let i = 0;
    let block = 0;
    // The blob follows the LIVE block nearest the squad: that is the one the camera is looking at
    // (the HUD threat is chosen by the same proximity rule, `enemyThreat`).
    let blob: { dist: number; shown: number; x: number; z: number; cols: number } | null = null;
    for (const enemy of sim.enemies) {
      if (enemy.engaged || enemy.count <= 0) {
        block++;
        continue;
      }
      const shown = Math.min(enemy.count, this.maxInstances);
      const spacing = ENEMY_FORMATION_SPACING;
      const enemyX = toWorldX(enemy.x);
      // Pure facing: turn the authored +z model toward the squad it is shooting at. Babylon stores
      // Matrix.m row-major and the thin-instance shader transforms vertices with the ROW-VECTOR
      // convention v' = v·M, so the model's +z axis lands in the matrix's THIRD ROW: for
      // RotationY(θ), row 2 = (sinθ, 0, cosθ) (verified against Matrix.RotationYToRef and
      // Vector3.TransformNormal). Forward = (sin yaw, 0, cos yaw) must equal the normalized
      // direction to the squad, hence yaw = atan2(dx, dz) with dx = squadX − enemyX.
      const yaw = Math.atan2(squadX - enemyX, sim.squadZ - enemy.z);
      const blockStart = i;
      const cols = enemyBlockColumns(enemy.halfWidth);
      for (let row = 0; i < this.maxInstances && row < Math.ceil(shown / cols) + 1; row++) {
        const inRow = Math.min(cols, shown - row * cols);
        for (let c = 0; c < inRow && i < this.maxInstances; c++, i++) {
          const lateral = (c - (inRow - 1) / 2) * spacing;
          const phase = stableHash(block * 16 + row, c);
          this.baked?.slot(i, phase, 0.6, true);
          const size = stableHash(block * 16 + row, c, 1);
          const scale = 1 - MOTION.scaleJitter + 2 * MOTION.scaleJitter * size;
          this.scratchScale.set(scale, scale, scale);
          this.scratchPos.set(enemyX + lateral, SQUAD_BASE_Y + bobY(t, phase, ramp), enemy.z - row * spacing * ENEMY_ROW_DEPTH_FACTOR);
          Quaternion.RotationYawPitchRollToRef(yaw, 0, rollRad(t, phase, ramp), this.scratchRot);
          Matrix.ComposeToRef(this.scratchScale, this.scratchRot, this.scratchPos, this.tmp);
          this.tmp.copyToArray(this.matrices, i * 16);
          writeTint(this.tints, i, stableHash(block * 16 + row, c, 2), stableHash(block * 16 + row, c, 3));
        }
      }
      const drawn = i - blockStart; // what the instance budget actually left for this block
      if (drawn > 0) {
        const dist = Math.abs(enemy.z - sim.squadZ);
        if (!blob || dist < blob.dist) blob = { dist, shown: drawn, x: enemyX, z: enemy.z, cols };
      }
      block++;
    }
    this.mesh.thinInstanceCount = i;
    this.baked?.update(t);
    if (i > 0) {
      this.mesh.thinInstanceBufferUpdated('matrix');
      this.mesh.thinInstanceBufferUpdated('color');
      this.mesh.thinInstanceRefreshBoundingInfo(true);
    }
    this.updateBlob(blob, sim);
  }

  /**
   * Ground the nearest live block with the pooled blob (impl gate F2, plan 05 §P5.6: "blob stays for
   * enemies on all tiers"). Closed-form: position and scale are pure functions of the snapshot, so
   * the disc is pause-frozen exactly like the instances it stands for. No live block ⇒ no disc.
   */
  private updateBlob(nearest: { shown: number; x: number; z: number; cols: number } | null, sim: SimState): void {
    let count = 0;
    this.enemyContact = nearest !== null;
    const place = (x: number, z: number, width: number, depth: number): void => {
      const scale = blobScaling(width, depth);
      Matrix.ScalingToRef(scale.x, 1, scale.z, this.tmp);
      this.tmp.setTranslationFromFloats(x, 0.025, z);
      this.tmp.copyToArray(this.contacts, count++ * 16);
    };
    if (nearest) {
      const footprint = enemyBlockFootprint(nearest.shown, nearest.cols);
      place(nearest.x, nearest.z - footprint.depth / 2, footprint.width, footprint.depth);
    }
    const nearby = (z: number): boolean => z >= sim.squadZ - 10 && z <= sim.squadZ + 70;
    this.contactLevel?.crates.forEach((spec, i) => {
      if (!sim.crates[i]?.broken && nearby(spec.z)) place(toWorldX(spec.x), spec.z, 0.6, 0.6);
    });
    this.contactLevel?.obstacles.forEach((spec, i) => {
      if (sim.obstacles[i]?.broken || !nearby(spec.z)) return;
      const width = 2 * toWorldX(spec.halfWidth);
      place(toWorldX(spec.x), spec.z, Math.max(0.1, width - 0.35), spec.kind === 'barrel' ? Math.max(0.1, width - 0.35) : 0.35);
    });
    this.shadow.thinInstanceCount = count;
    this.shadow.setEnabled(count > 0);
    if (count > 0) {
      this.shadow.thinInstanceBufferUpdated('matrix');
      this.shadow.thinInstanceRefreshBoundingInfo(true);
    }
  }

  /** The first shared contact instance belongs to the nearest live enemy block. */
  debugBlob(): { name: string; enabled: boolean; visible: boolean; x: number; z: number; width: number; depth: number } {
    return {
      name: this.shadow.name,
      enabled: this.enemyContact && this.shadow.isEnabled(),
      visible: this.enemyContact && this.shadow.isVisible,
      x: this.enemyContact ? this.contacts[12]! : 0,
      z: this.enemyContact ? this.contacts[14]! : 0,
      width: this.enemyContact ? this.contacts[0]! * BLOB_AUTHORED_GROUND : 0,
      depth: this.enemyContact ? this.contacts[10]! * BLOB_AUTHORED_GROUND : 0,
    };
  }

  /** Test handle: the thin-instance tint buffer (RGBA, stride 4). */
  debugTints(): Float32Array {
    return this.tints;
  }

  /** Test/probe handle: a copy of one instance matrix (16 floats, row-major). */
  debugMatrix(i: number): number[] {
    return Array.from(this.matrices.subarray(i * 16, i * 16 + 16));
  }

  /** Probe: thin-instance z extents (plan 03 Phase 1). */
  debugThinInstances(): { name: string; count: number; zMin: number | null; zMax: number | null } {
    let zMin: number | null = null;
    let zMax: number | null = null;
    for (let i = 0; i < this.mesh.thinInstanceCount; i++) {
      const z = this.matrices[i * 16 + 14];
      zMin = zMin === null ? z : Math.min(zMin, z);
      zMax = zMax === null ? z : Math.max(zMax, z);
    }
    return { name: 'enemy-soldier', count: this.mesh.thinInstanceCount, zMin, zMax };
  }
}

/**
 * Authored spike-strip dimensions (plan 15 §1, design decision 1). Metres; one strip per
 * `level.hazards` entry, built by `buildSpikeStrip`: a dark steel base rail + a deterministic
 * 2-row × k-column grid of conical spikes (k = ceil(bandWidth / 1.1), min 3, tips leaning a few
 * degrees — alternately, no RNG), all merged into ONE mesh named `hazard-z{z}` — the name
 * `Hazards.update`/`reset`/`debugHazards` key on. Per-strip triangle budget ≤ 600 (test-asserted).
 */
export const SPIKE_STRIP = {
  /** Band depth, inherited from the plan-04 flat band. */
  depth: 2.4,
  /** Base rail: bottom sits at 0.06 — above the 0.045 glow quad — up to 0.18. */
  railBottomY: 0.06,
  railHeight: 0.12,
  /** Spikes: ~0.55 m six-sided cones, one every ≤ 1.1 m across the band, min 3 columns. */
  spikeHeight: 0.55,
  spikeRadius: 0.14,
  spikeTessellation: 6,
  spikePitch: 1.1,
  minColumns: 3,
  /** The two spike rows along the band depth, symmetric about its centre. */
  rowZs: [-0.6, 0.6] as const,
  /** Tips lean a few degrees: roll left/right by column parity, pitch fore/aft by row parity. */
  rollTilt: (3 * Math.PI) / 180,
  pitchTilt: (2 * Math.PI) / 180,
  /** Per-strip triangle budget (rail + every spike; asserted by test). */
  triBudget: 600,
  /** The glow quad's y, under the rail — and how much wider than the band it is, per side.
   * Fix round: 0.15 → 0.5 m/side at alpha 0.8 — at the inspect pause distances (10–20 m, ~11°
   * grazing) the old sliver was invisible edge-on behind the rail (0 red pixels measured); the
   * wider/brighter frame keeps the long-distance danger read the flat band used to carry. */
  glowY: 0.045,
  glowOverhang: 0.5,
  glowAlpha: 0.8,
  /** The vertical glow face's height at the band's near edge (the always-readable read). */
  glowFenceH: 0.3,
} as const;

/**
 * Author one hazard band: rail box + spike cones merged into a single mesh (the `buildObstacle`
 * pattern — authored primitives, one draw). Columns are inset so every spike — tips leaning —
 * stays inside the band: the deck-width assert ("what you see is what hits you", plan 12) must
 * measure the RAIL, never stray geometry. The caller owns naming and material.
 */
function buildSpikeStrip(scene: Scene, name: string, bandWidth: number): Mesh {
  const parts: Mesh[] = [
    box(
      scene,
      `${name}-rail`,
      bandWidth,
      SPIKE_STRIP.railHeight,
      SPIKE_STRIP.depth,
      0,
      SPIKE_STRIP.railBottomY + SPIKE_STRIP.railHeight / 2,
      0,
    ),
  ];
  const k = Math.max(SPIKE_STRIP.minColumns, Math.ceil(bandWidth / SPIKE_STRIP.spikePitch));
  const inset = SPIKE_STRIP.spikeRadius + SPIKE_STRIP.spikeHeight * Math.sin(SPIKE_STRIP.rollTilt) + 0.01;
  const span = bandWidth - 2 * inset;
  for (let row = 0; row < SPIKE_STRIP.rowZs.length; row++) {
    for (let col = 0; col < k; col++) {
      const spike = MeshBuilder.CreateCylinder(
        `${name}-spike-${row}-${col}`,
        {
          diameterTop: 0,
          diameterBottom: SPIKE_STRIP.spikeRadius * 2,
          height: SPIKE_STRIP.spikeHeight,
          tessellation: SPIKE_STRIP.spikeTessellation,
        },
        scene,
      );
      spike.position.set(
        k === 1 ? 0 : -span / 2 + (span * col) / (k - 1),
        SPIKE_STRIP.railBottomY + SPIKE_STRIP.railHeight + SPIKE_STRIP.spikeHeight / 2,
        SPIKE_STRIP.rowZs[row],
      );
      spike.rotation.set(
        row % 2 === 0 ? SPIKE_STRIP.pitchTilt : -SPIKE_STRIP.pitchTilt,
        0,
        col % 2 === 0 ? SPIKE_STRIP.rollTilt : -SPIKE_STRIP.rollTilt,
      );
      parts.push(spike);
    }
  }
  const mesh = Mesh.MergeMeshes(parts, true, true)!;
  mesh.name = name;
  return mesh;
}

/**
 * Static hazards (plan 04 P1.4; plan 15 §1): each `level.hazards` entry is an authored SPIKE
 * STRIP (see `SPIKE_STRIP`). The red glow survives as a SEPARATE, wider quad
 * `hazard-glow-z{z}` under the rail — the long-distance danger read AND the hit-flash carrier.
 * Each prop stays visible until the squad crosses its z (one-shot latch). A crossed-and-hit prop
 * flashes the GLOW red for FLASH_SECONDS — the hit feedback — then both meshes are disabled; a
 * cleanly dodged prop just goes away. Bullets never interact with them: they are ground-level
 * props below the torso-height firing line.
 */
interface HazardVisual {
  /** The merged spike strip — the mesh `debugHazards` names and reports `active` for. */
  strip: Mesh;
  /** The wider glow quad under the rail; the hit flash pulses THIS mesh + its material. */
  glow: Mesh;
  mat: StandardMaterial;
  z: number;
  latched: boolean;
  flash: number;
  /** The glow material's resting alpha (built at 0.55); the hit flash writes over it. */
  baseAlpha: number;
}

/** Footprint outline (plan 13): metres in front of / behind squadZ, and the line width. */
export const FOOTPRINT_FRONT = 0.6;
export const FOOTPRINT_BACK = MAX_FORMATION_DEPTH + 0.2;
export const FOOTPRINT_LINE = 0.1;

/**
 * Plan 12/13: the squad's hit width as an outline on the deck around the crowd — squadX ±
 * squadHalfX(n), the interval every hazard, obstacle, blast, block, pickup and slam tests. It grows
 * with the squad and stays on through the boss fight: a slam lands when the telegraph touches it.
 * One mesh, four thin instances (front, back, left, right), so it costs one draw call.
 */
export class Footprint {
  readonly mesh: Mesh;
  /** The four strip matrices (front, back, left, right); read by the tests. */
  readonly matrices = new Float32Array(4 * 16);
  private readonly tmp = Matrix.Identity();

  constructor(scene: Scene, materials: RenderMaterials) {
    this.mesh = MeshBuilder.CreateGround('squad-footprint', { width: 1, height: 1 }, scene);
    this.mesh.isPickable = false;
    const mat = materials.glow(new Color3(0.35, 0.85, 1), 0.55);
    mat.alphaMode = Constants.ALPHA_ADD;
    mat.backFaceCulling = false;
    this.mesh.material = mat;
    this.mesh.thinInstanceSetBuffer('matrix', this.matrices, 16, false);
  }

  update(sim: SimState): void {
    const on = sim.phase === 'run' || sim.phase === 'boss';
    this.mesh.setEnabled(on);
    if (!on) return;
    const half = squadHalfX(sim.n) * BRIDGE_HALF_WIDTH;
    const x = toWorldX(sim.squadX);
    const front = sim.squadZ + FOOTPRINT_FRONT;
    const back = sim.squadZ - FOOTPRINT_BACK;
    const t = FOOTPRINT_LINE;
    const strips: [number, number, number, number][] = [
      [x, front, 2 * half + t, t],
      [x, back, 2 * half + t, t],
      [x - half, (front + back) / 2, t, front - back],
      [x + half, (front + back) / 2, t, front - back],
    ];
    strips.forEach(([sx, sz, w, d], i) => {
      Matrix.ScalingToRef(w, 1, d, this.tmp);
      this.tmp.setTranslationFromFloats(sx, 0.04, sz);
      this.tmp.copyToArray(this.matrices, i * 16);
    });
    this.mesh.thinInstanceBufferUpdated('matrix');
    this.mesh.thinInstanceRefreshBoundingInfo(true);
  }
}

/**
 * Plan 13 firepower pickups: one gold prop per enemy block at (x, z + PICKUP_AHEAD), shown only
 * while the sim says the drop is 'live' (block wiped, not yet crossed). Spin is f(sim.t): pause-frozen.
 */
export class Pickups {
  private readonly meshes: Mesh[];

  constructor(scene: Scene, materials: RenderMaterials, level: { enemies: { z: number; x: number }[] }) {
    const mat = materials.glow(new Color3(1, 0.78, 0.2), 1);
    this.meshes = level.enemies.map((e) => {
      const rounds: Mesh[] = [];
      for (const x of [-0.17, 0, 0.17]) {
        const casing = MeshBuilder.CreateCylinder('pickup-casing', { diameter: 0.13, height: 0.5, tessellation: 8 }, scene);
        casing.position.set(x, 0.85, 0);
        const tip = MeshBuilder.CreateCylinder('pickup-tip', { diameterBottom: 0.13, diameterTop: 0, height: 0.14, tessellation: 8 }, scene);
        tip.position.set(x, 1.17, 0);
        rounds.push(casing, tip);
      }
      const clip = MeshBuilder.CreateBox('pickup-clip', { width: 0.52, height: 0.1, depth: 0.18 }, scene);
      clip.position.y = 0.58;
      rounds.push(clip);
      const ring = MeshBuilder.CreateTorus('pickup-ring', { diameter: 1.6, thickness: 0.08, tessellation: 24 }, scene);
      ring.position.y = 0.05;
      const mesh = Mesh.MergeMeshes([...rounds, ring], true)!;
      mesh.name = `pickup-z${e.z}`;
      mesh.material = mat;
      mesh.position.set(toWorldX(e.x), 0, e.z + PICKUP_AHEAD);
      mesh.setEnabled(false);
      return mesh;
    });
  }

  update(sim: SimState): void {
    this.meshes.forEach((mesh, i) => {
      const live = sim.enemies[i]?.drop === 'live';
      mesh.setEnabled(live);
      if (live) mesh.rotation.y = sim.t * 2.5;
    });
  }
}

export class Hazards {
  private static readonly FLASH_SECONDS = 0.4;
  private readonly visuals: HazardVisual[] = [];

  constructor(scene: Scene, materials: RenderMaterials, level: LevelSpec) {
    for (const hazard of level.hazards) {
      // True band width (plan 12): the squad's own width is the `Footprint` outline, so a hit reads
      // as "the outline touches the strip", the same rule as a barrel.
      const width = hazard.halfWidth * 2 * BRIDGE_HALF_WIDTH;
      const strip = buildSpikeStrip(scene, `hazard-z${hazard.z}`, width);
      strip.position.set(toWorldX(hazard.x), 0, hazard.z);
      strip.material = materials.metal; // shared weathered steel; the flash never touches it
      // The glow: one material per band (a flash must pulse only this band). Captured per visual,
      // not hardcoded: `update` parks the alpha at the 0.25 tail of the flash and `reset` has to
      // put back whatever this prop actually started at.
      const glow = MeshBuilder.CreateGround(
        `hazard-glow-z${hazard.z}`,
        { width: width + 2 * SPIKE_STRIP.glowOverhang, height: SPIKE_STRIP.depth },
        scene,
      );
      glow.position.set(toWorldX(hazard.x), SPIKE_STRIP.glowY, hazard.z);
      // Fix round 2: a flat additive quad is ~invisible at the inspect grazing angles (measured
      // 0 red pixels at 18 m) — the glow also carries a VERTICAL face at the band's near edge
      // (0.3 m, the hazard-fence read) so the danger band reads from any distance/angle. Same
      // material ⇒ merged into the one glow mesh: no extra draw, one flash carrier.
      const fence = MeshBuilder.CreatePlane(`hazard-glow-fence-z${hazard.z}`, { width: width + 2 * SPIKE_STRIP.glowOverhang, height: SPIKE_STRIP.glowFenceH }, scene);
      fence.position.set(toWorldX(hazard.x), SPIKE_STRIP.glowFenceH / 2 + 0.02, hazard.z - SPIKE_STRIP.depth / 2);
      const glowMesh = Mesh.MergeMeshes([glow, fence], true, true, undefined, false, false)!;
      glowMesh.name = `hazard-glow-z${hazard.z}`;
      const mat = materials.glow(new Color3(0.86, 0.11, 0.06), SPIKE_STRIP.glowAlpha);
      mat.alphaMode = Constants.ALPHA_ADD;
      mat.backFaceCulling = false;
      glowMesh.material = mat;
      this.visuals.push({ strip, glow: glowMesh, mat, z: hazard.z, latched: false, flash: 0, baseAlpha: mat.alpha });
    }
  }

  /** Sync from sim: latch once, flash the glow red on a hit, then hide both meshes. */
  update(sim: SimState, dt: number): void {
    for (const visual of this.visuals) {
      const state = sim.hazards.find((h) => h.z === visual.z);
      if (!state) continue;
      if (state.latched && !visual.latched) {
        visual.latched = true;
        if (state.hit) visual.flash = Hazards.FLASH_SECONDS; // the red flash is the hit feedback
      }
      if (visual.flash > 0) {
        visual.flash = Math.max(0, visual.flash - dt);
        visual.mat.alpha = 0.25 + 0.75 * (visual.flash / Hazards.FLASH_SECONDS); // on the GLOW quad
      }
      const show = visual.flash > 0 || !visual.latched;
      visual.strip.setEnabled(show);
      visual.glow.setEnabled(show);
    }
  }

  /**
   * Restart hook (plan 04 P1.7): the one-shot latch lives in the sim, but the props hide
   * themselves on the latch, so a fresh run needs them back — BOTH meshes, plus the glow's
   * resting alpha. Every level ships hazards since plan 15, so every restart relies on this.
   */
  reset(): void {
    for (const visual of this.visuals) {
      visual.latched = false;
      visual.flash = 0;
      // The flash leaves the alpha at its 0.25 tail; without this a restarted course comes back
      // permanently dimmer than a fresh one (plan 04 fix round).
      visual.mat.alpha = visual.baseAlpha;
      visual.strip.setEnabled(true);
      visual.glow.setEnabled(true);
    }
  }

  /** Probe (P1.4): hazard meshes + latched state, so the gate tooling sees the truth per shot. */
  debugHazards(sim: SimState): { name: string; z: number; active: boolean; latched: boolean; hit: boolean }[] {
    return this.visuals.map((visual) => {
      const state = sim.hazards.find((h) => h.z === visual.z);
      return {
        name: visual.strip.name,
        z: visual.z,
        active: visual.strip.isEnabled(),
        latched: state?.latched ?? false,
        hit: state?.hit ?? false,
      };
    });
  }
}

export class Crate {
  readonly mesh: Mesh;
  constructor(scene: Scene, materials: RenderMaterials, spec: { z: number; x: number }) {
    const body = MeshBuilder.CreateBox('crate-body', { width: 1, height: 0.9, depth: 1 }, scene);
    body.position.y = -0.05;
    const lid = MeshBuilder.CreateBox('crate-lid', { width: 1, height: 0.12, depth: 1 }, scene);
    lid.position.y = 0.44;
    const parts = [body, lid];
    for (const x of [-0.3, 0.3]) parts.push(box(scene, 'crate-latch', 0.09, 0.2, 0.035, x, 0.31, -0.495));
    this.mesh = Mesh.MergeMeshes(parts, true, true)!;
    this.mesh.name = 'crate';
    this.mesh.material = materials.metal;
    this.mesh.position.set(toWorldX(spec.x), 0.5, spec.z);
    const stripe = MeshBuilder.CreateBox('crate-band', { width: 1.04, height: 0.1, depth: 1.04 }, scene);
    stripe.position.set(0, 0.25, 0);
    const markings = [stripe];
    for (const x of [-0.09, 0, 0.09]) {
      const round = MeshBuilder.CreateCylinder('crate-ammo-mark', { diameter: 0.045, height: 0.19, tessellation: 6 }, scene);
      round.position.set(x, -0.07, -0.51);
      const tip = MeshBuilder.CreateCylinder('crate-ammo-tip', { diameterBottom: 0.045, diameterTop: 0, height: 0.045, tessellation: 6 }, scene);
      tip.position.set(x, 0.0475, -0.51);
      markings.push(round, tip);
    }
    markings.push(box(scene, 'crate-ammo-clip', 0.27, 0.03, 0.02, 0, -0.18, -0.51));
    const insignia = Mesh.MergeMeshes(markings, true, true)!;
    insignia.name = 'crate-stripe';
    insignia.material = materials.glow(new Color3(1.0, 0.75, 0.2), 1);
    insignia.parent = this.mesh;
  }
  update(broken: boolean): void {
    this.mesh.setEnabled(!broken);
  }
}

/**
 * Obstacles (plan 09): one thin-instanced mesh per kind, authored 1 m wide from primitives and
 * scaled per instance to the sim's hit interval (2 × toWorldX(halfWidth)) — what you see is what
 * hits you, the Hazards precedent. Pure placement from `sim.obstacles`: no dt, no render-side
 * latch; a broken obstacle simply stops being an instance (the HP bar carries the damage read).
 * Not a shadow caster, like the crates.
 */
export const OBSTACLE_DRAW_Z = 150;
/** Behind the squad an intact obstacle stays drawn this far (it is passed, not gone). */
const OBSTACLE_BEHIND_Z = 10;
const OBSTACLE_LOOK: Record<ObstacleKind, { tint: readonly [number, number, number]; round: boolean }> = {
  // Red oxide re-exported from barrelDims (plan 14: one authored source); round ⇒ depth scales with width.
  barrel: { tint: BARREL_DIMS.tint, round: true },
  barricade: { tint: [0.4, 0.43, 0.24], round: false }, // olive drab
};

function buildObstacle(scene: Scene, material: PBRMaterial, kind: ObstacleKind): Mesh {
  const name = `obstacle-${kind}`;
  const parts: Mesh[] = [];
  if (kind === 'barrel') {
    // Plan 14 P14.2 — the detailed 1.2 m fuel drum, authored in BARREL_DIMS (the single source). The
    // pivot is the deck (minY = 0) and the mesh's WIDEST x-extent is the hoop OD, which is exactly
    // what the instance scale maps onto the sim hit interval 2 × toWorldX(halfWidth) ("what you see
    // is what hits you"): the body and lid sit inset under the hoops, the bungs never reach the hoop
    // radius. The lid's thickness is the shard table's lidThickness — the P14.3 lid-ring shard is a
    // piece of this lid, so the two lanes cannot drift. Tessellation 24 for the round read (bungs 12:
    // caps, not silhouette) keeps the merged drum a few hundred triangles, one thin-instanced draw.
    const tess = 24;
    const bodyHeight = BARREL_DIMS.height - BARREL_DIMS.shard.lidThickness;
    const body = MeshBuilder.CreateCylinder(`${name}-body`, { diameter: BARREL_DIMS.bodyDiameter, height: bodyHeight, tessellation: tess }, scene);
    body.position.y = bodyHeight / 2;
    parts.push(body);
    BARREL_DIMS.hoopYs.forEach((y, i) => {
      const hoop = MeshBuilder.CreateCylinder(
        `${name}-hoop-${i}`,
        { diameter: BARREL_DIMS.hoopOuterDiameter, height: BARREL_DIMS.hoopHeight, tessellation: tess },
        scene,
      );
      hoop.position.y = y; // bottom chime, mid rolling hoop, top chime
      parts.push(hoop);
    });
    const lid = MeshBuilder.CreateCylinder(
      `${name}-lid`,
      { diameter: BARREL_DIMS.lidDiameter, height: BARREL_DIMS.shard.lidThickness, tessellation: tess },
      scene,
    );
    lid.position.y = BARREL_DIMS.height - BARREL_DIMS.shard.lidThickness / 2; // top face = ground-to-lid height
    parts.push(lid);
    BARREL_DIMS.bungOffsets.forEach((x, i) => {
      const bung = MeshBuilder.CreateCylinder(`${name}-bung-${i}`, { diameter: BARREL_DIMS.bungDiameter, height: BARREL_DIMS.bungHeight, tessellation: 12 }, scene);
      bung.position.set(x, BARREL_DIMS.height + BARREL_DIMS.bungHeight / 2, 0); // protrudes off the lid
      parts.push(bung);
    });
  } else {
    // Two upright steel plates on a pair of splayed legs, 1.3 m tall.
    parts.push(box(scene, `${name}-plate`, 1, 0.55, 0.08, 0, 0.45, 0));
    parts.push(box(scene, `${name}-plate`, 1, 0.55, 0.08, 0, 1.03, 0));
    for (const x of [-0.38, 0.38]) parts.push(box(scene, `${name}-leg`, 0.07, 1.3, 0.6, x, 0.65, 0.1));
  }
  const mesh = Mesh.MergeMeshes(parts, true, true)!;
  mesh.name = name;
  mesh.material = material;
  return mesh;
}

export class Obstacles {
  private readonly kinds: { kind: ObstacleKind; mesh: Mesh; matrices: Float32Array }[] = [];
  private readonly tmp = Matrix.Identity();
  private readonly upright = Quaternion.Identity();
  private readonly scratchScale = new Vector3(1, 1, 1);
  private readonly scratchPos = new Vector3(0, 0, 0);

  constructor(scene: Scene, materials: RenderMaterials, private readonly level: LevelSpec) {
    // Only the kinds this level ships get a mesh (plan 14/15: L1 ships barrels, L2 adds a
    // barricade — the empty-kind case simply costs nothing).
    for (const kind of Object.keys(OBSTACLE_LOOK) as ObstacleKind[]) {
      const count = level.obstacles.filter((o) => o.kind === kind).length;
      if (count === 0) continue;
      const mesh = buildObstacle(scene, materials.metal, kind);
      const matrices = new Float32Array(count * 16);
      const tints = new Float32Array(count * 4);
      const [r, g, b] = OBSTACLE_LOOK[kind].tint;
      for (let i = 0; i < count; i++) tints.set([r, g, b, 1], i * 4);
      mesh.thinInstanceSetBuffer('matrix', matrices, 16, false);
      mesh.thinInstanceSetBuffer('color', tints, 4, true);
      mesh.thinInstanceCount = 0;
      mesh.setEnabled(false);
      this.kinds.push({ kind, mesh, matrices });
    }
  }

  update(sim: SimState): void {
    for (const k of this.kinds) {
      let i = 0;
      for (const o of sim.obstacles) {
        const ahead = o.z - sim.squadZ;
        if (o.kind !== k.kind || o.broken || ahead < -OBSTACLE_BEHIND_Z || ahead > OBSTACLE_DRAW_Z) continue;
        const width = 2 * toWorldX(o.halfWidth);
        this.scratchScale.set(width, 1, OBSTACLE_LOOK[k.kind].round ? width : 1);
        this.scratchPos.set(toWorldX(o.x), 0, o.z);
        Matrix.ComposeToRef(this.scratchScale, this.upright, this.scratchPos, this.tmp);
        this.tmp.copyToArray(k.matrices, i * 16);
        i++;
      }
      k.mesh.thinInstanceCount = i;
      k.mesh.setEnabled(i > 0);
      if (i > 0) {
        k.mesh.thinInstanceBufferUpdated('matrix');
        k.mesh.thinInstanceRefreshBoundingInfo(true);
      }
    }
  }

  /** Probe (plan 09 P9.5): every obstacle's sim truth, plus whether its kind's mesh is drawing. */
  debugObstacles(sim: SimState): {
    kind: ObstacleKind; z: number; x: number; hp: number; maxHp: number; broken: boolean; passed: boolean; meshActive: boolean;
  }[] {
    return sim.obstacles.map((o, i) => ({
      kind: o.kind,
      z: o.z,
      x: o.x,
      hp: o.hp,
      maxHp: this.level.obstacles[i].hp,
      broken: o.broken,
      passed: o.passed,
      meshActive: this.kinds.find((k) => k.kind === o.kind)?.mesh.isEnabled() ?? false,
    }));
  }

  /** Test handle: one kind's live instance matrices (16 floats each, row-major). */
  debugMatrices(kind: ObstacleKind): number[] {
    const k = this.kinds.find((entry) => entry.kind === kind);
    return k ? Array.from(k.matrices.subarray(0, k.mesh.thinInstanceCount * 16)) : [];
  }
}

/**
 * Authored boss dimensions (plan 05 §P5.4). Metres, model faces −z (toward the squad, which
 * advances from −z), feet pivot at y = 0 so breathing/lean rotate and scale about the deck.
 */

/**
 * The brute: heavy trapezoid torso (square frustum + boxed chest/back plates), oversized shoulder
 * pauldrons, thick arms, head sunk between the shoulders, studded club. One MergeMeshes product,
 * one material, same UV-atlas discipline as the soldier (the boss rows of the shared tile table).
 * Budget 8 000 triangles (§P5.4) — measured, asserted in actors.boss.test.ts.
 */
export function buildBoss(scene: Scene, material: PBRMaterial, name: string): BossGeometry {
  return authoredGeometry('brute', scene, material, name);
}

/** Eye/vent slit intensity: a pure function of the snapshot (no dt state). Smoulder while the boss
 * fights, ramping to a fast pulse over the slam telegraph, dark outside the fight (plan 08 G5). */
export const BOSS_GLOW_IDLE = 0.3;
export function bossGlow(sim: SimState, spec: { slamPeriod: number; slamTelegraph: number }): number {
  if (sim.phase !== 'boss') return 0;
  if (!sim.slam || sim.slam.active !== 'telegraph') return BOSS_GLOW_IDLE;
  const p = clamp01((sim.slam.t - (spec.slamPeriod - spec.slamTelegraph)) / spec.slamTelegraph);
  return BOSS_GLOW_IDLE + (1 - BOSS_GLOW_IDLE) * p * (0.75 + 0.25 * Math.sin(sim.t * 18));
}

/** Push `bossGlow` into the shared boss material. Needs the emissive map (absent under NullEngine,
 * where an emissive colour would light the whole flat-albedo body). HDR gain 4 feeds the bloom. */
export function applyBossGlow(mat: PBRMaterial, sim: SimState, spec: { slamPeriod: number; slamTelegraph: number }): void {
  if (!mat.emissiveTexture) return;
  const g = 4 * bossGlow(sim, spec);
  mat.emissiveColor.copyFromFloats(g, g * 0.32, g * 0.08);
}

/**
 * Gates: portal frames + translucent panels + DynamicTexture number labels. Panels tint blue
 * (positive), red (negative), gold (multiplier). Labels live-update as bullets raise values.
 */
interface GateVisual {
  frame: Mesh;
  panel: Mesh;
  label: Mesh;
  /** Created lazily inside the 260 m window, disposed once the pair is passed (texture memory, plan 08 G3). */
  labelTex?: DynamicTexture;
  panelMat: StandardMaterial;
  side: 'left' | 'right';
  z: number;
}

/** Gate feedback envelopes (plan 08 G3): pure functions of sim-time age, so a paused frame is repeatable. */
export const GATE_PULSE_S = 0.15;
export const GATE_FLASH_S = 0.35;
const GATE_PANEL_ALPHA = 0.7; // peak (frame edges) of the gradient opacity texture
/** Label scale 1 → 1.12 → 1 over GATE_PULSE_S after a `gateShot`; 1 outside the window. */
export function gatePulse(age: number): number {
  return age < 0 || age >= GATE_PULSE_S ? 1 : 1 + 0.12 * Math.sin((Math.PI * age) / GATE_PULSE_S);
}
/** Passed-panel flash 1 → 0 over GATE_FLASH_S after a `gatePass`; 0 outside the window. */
export function gateFlash(age: number): number {
  return age < 0 || age >= GATE_FLASH_S ? 0 : 1 - age / GATE_FLASH_S;
}

export class Gates {
  private readonly visuals: GateVisual[] = [];
  /** Sim-stamped event times (ev.t), keyed `z|side`; evaluated as f(sim.t − t) in update. */
  private readonly shotAt = new Map<string, number>();
  private readonly passAt = new Map<number, { side: 'left' | 'right'; t: number }>();
  private readonly fadeTex: DynamicTexture;

  constructor(
    private readonly scene: Scene,
    _materials: RenderMaterials, // frame no longer uses the shared metal PBR (G3 vertex-colour emissive)
    level: LevelSpec,
  ) {
    // Shared vertical opacity ramp: opaque at the frame edges, clear in the middle (energy field).
    this.fadeTex = new DynamicTexture('gate-fade', { width: 4, height: 64 }, scene, false);
    const fctx = this.fadeTex.getContext() as CanvasRenderingContext2D;
    const grad = fctx.createLinearGradient(0, 0, 0, 64);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.5, 'rgba(255,255,255,0.12)');
    grad.addColorStop(1, 'rgba(255,255,255,1)');
    fctx.fillStyle = grad;
    fctx.fillRect(0, 0, 4, 64);
    this.fadeTex.hasAlpha = true;
    this.fadeTex.update();
    for (const pair of level.gates) {
      this.visuals.push(this.build(pair.z, 'left', pair.left));
      this.visuals.push(this.build(pair.z, 'right', pair.right));
    }
  }

  private build(z: number, side: 'left' | 'right', spec: GateSpec): GateVisual {
    const { scene } = this;
    const centerX = toWorldX(side === 'left' ? -0.5 : 0.5);
    const width = BRIDGE_HALF_WIDTH;

    const postL = MeshBuilder.CreateBox(`gate-${z}-${side}-pl`, { width: 0.28, height: 3.6, depth: 0.28 }, scene);
    postL.position.set(centerX - width / 2, 1.8, z);
    const postR = postL.clone(`gate-${z}-${side}-pr`);
    postR.position.x = centerX + width / 2;
    const top = MeshBuilder.CreateBox(`gate-${z}-${side}-top`, { width: width + 0.28, height: 0.3, depth: 0.3 }, scene);
    top.position.set(centerX, 3.6, z);
    const frame = Mesh.MergeMeshes([postL, postR, top], true, true, undefined, false, false)!;
    // Flat vertex-colour emissive (white emissive × vertex colour): the top strip carries the gate colour.
    frame.material = this.frameMat ??= Gates.makeFrameMat(scene);

    const panelMat = new StandardMaterial(`gate-${z}-${side}-panel`, scene);
    panelMat.alpha = GATE_PANEL_ALPHA;
    panelMat.opacityTexture = this.fadeTex;
    panelMat.backFaceCulling = false;
    panelMat.disableLighting = true;
    const panel = MeshBuilder.CreatePlane(`gate-${z}-${side}-panel`, { width: width, height: 3.3 }, scene);
    panel.position.set(centerX, 1.8, z);
    panel.material = panelMat;

    const label = MeshBuilder.CreatePlane(`gate-${z}-${side}-label`, { width: 3.4, height: 1.5 }, scene);
    label.position.set(centerX, 2.4, z - 0.25);

    const visual: GateVisual = { frame, panel, label, panelMat, side, z };
    this.paint(visual, spec.value, spec.kind); // colours only; the label texture is made on first sight
    return visual;
  }

  private ensureLabel(visual: GateVisual): void {
    if (visual.labelTex) return;
    const { scene } = this;
    const name = `gate-${visual.z}-${visual.side}`;
    const tex = new DynamicTexture(`${name}-tex`, { width: 512, height: 224 }, scene, true);
    tex.anisotropicFilteringLevel = 4;
    tex.hasAlpha = true;
    const mat = new StandardMaterial(`${name}-label`, scene);
    mat.diffuseColor = Color3.Black();
    mat.emissiveTexture = tex; // emissive-only: glows through haze, feeds bloom
    mat.opacityTexture = tex;
    mat.fogEnabled = false;
    mat.disableLighting = true;
    mat.backFaceCulling = false;
    visual.label.material = mat;
    visual.labelTex = tex;
    this.painted.delete(visual); // force a repaint
  }

  private disposeLabel(visual: GateVisual): void {
    if (!visual.labelTex) return;
    visual.label.material?.dispose(); // material first, then its texture
    visual.labelTex.dispose();
    visual.labelTex = undefined;
    visual.label.material = null;
  }

  private paint(visual: GateVisual, value: number, kind: GateSpec['kind'] = 'num'): void {
    const printed = kind === 'num' && value < 0;
    const positive = !printed;
    // Divider gates read violet, multipliers gold, adds blue, subs red, RATE+ green (genre legibility).
    const color =
      kind === 'mul' ? '#ffd257' : kind === 'div' ? '#b78cff' : kind === 'rate' ? '#6fe39a' : positive ? '#7fc4ff' : '#ff7a6b';
    visual.panelMat.emissiveColor = Color3.FromHexString(
      kind === 'mul' ? '#8a6d1d' : kind === 'div' ? '#4b3a7a' : kind === 'rate' ? '#1d6a3e' : positive ? '#1d4c7a' : '#7a241d',
    );
    const col = Color3.FromHexString(color);
    const cols: number[] = [];
    const pos = visual.frame.getVerticesData(VertexBuffer.PositionKind)!;
    for (let i = 0; i < pos.length; i += 3) {
      if (pos[i + 1] > 3.44) cols.push(col.r, col.g, col.b, 1);
      else cols.push(0.3, 0.32, 0.36, 1);
    }
    if (visual.frame.isVerticesDataPresent(VertexBuffer.ColorKind)) visual.frame.updateVerticesData(VertexBuffer.ColorKind, cols);
    else visual.frame.setVerticesData(VertexBuffer.ColorKind, cols, true);
    const tex = visual.labelTex;
    if (!tex) return;
    const ctx = tex.getContext() as CanvasRenderingContext2D;
    ctx.clearRect(0, 0, 512, 224);
    ctx.font = 'bold 150px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = color;
    ctx.shadowColor = 'rgba(0,0,0,0.7)';
    ctx.shadowBlur = 16;
    ctx.lineJoin = 'round';
    ctx.lineWidth = 14;
    ctx.strokeStyle = 'rgba(8,10,16,0.95)';
    const text = gateLabel({ kind, value });
    // Shrink to fit 470 px so a five-glyph label (RATE+) never clips the 512 px card.
    const width = ctx.measureText(text)?.width ?? 0; // undefined on the node test canvas stub
    if (width > 470) ctx.font = `bold ${Math.floor((150 * 470) / width)}px system-ui, sans-serif`;
    ctx.strokeText(text, 256, 118);
    ctx.fillText(text, 256, 118);
    tex.update();
  }

  private readonly painted = new Map<GateVisual, number>();
  private frameMat?: StandardMaterial;

  private static makeFrameMat(scene: Scene): StandardMaterial {
    const m = new StandardMaterial('gate-frame', scene);
    m.disableLighting = true;
    m.emissiveColor = new Color3(1, 1, 1);
    m.diffuseColor = Color3.Black();
    return m;
  }

  /** Record sim-stamped gate events (drained once per frame; `ev.t` keeps the phase frame-rate independent). */
  handleEvents(events: readonly SimEvent[]): void {
    for (const ev of events) {
      if (ev.type === 'gateShot') this.shotAt.set(`${ev.z}|${ev.side}`, ev.t);
      else if (ev.type === 'gatePass') this.passAt.set(ev.z, { side: ev.side, t: ev.t });
    }
  }

  /** Restart: drop recorded times (sim.t rewinds to 0). */
  reset(): void {
    this.shotAt.clear();
    this.passAt.clear();
  }

  /** Sync from sim: labels live-update as bullets raise values; visibility by distance. */
  update(sim: SimState): void {
    for (const visual of this.visuals) {
      const gate = sim.gates.find((g) => g.z === visual.z && g.side === visual.side);
      if (!gate) continue;
      const pass = this.passAt.get(gate.z);
      const passAge = pass ? sim.t - pass.t : -1;
      const flash = gateFlash(passAge);
      const visible = (!gate.consumed || flash > 0) && Math.abs(gate.z - sim.squadZ) < 260;
      visual.frame.setEnabled(visible);
      visual.panel.setEnabled(visible);
      visual.label.setEnabled(visible);
      if (!visible) {
        if (gate.consumed && flash === 0) this.disposeLabel(visual); // passed: free the texture
        continue;
      }
      this.ensureLabel(visual);
      // Pass feedback: the taken panel flashes, the other dims to 30 %.
      visual.panelMat.alpha = !pass || flash === 0 ? GATE_PANEL_ALPHA : pass.side === visual.side ? Math.min(1, GATE_PANEL_ALPHA + flash * 0.3) : GATE_PANEL_ALPHA * 0.3;
      visual.label.scaling.setAll(gatePulse(sim.t - (this.shotAt.get(`${visual.z}|${visual.side}`) ?? -1)));
      const value = gate.spec.kind === 'num' ? gate.value : gate.spec.value;
      if (this.painted.get(visual) !== value) {
        this.paint(visual, value, gate.spec.kind);
        this.painted.set(visual, value);
      }
    }
  }
}
